import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import { supabase } from '../../lib/supabase';
import { hasPermission, PERMISSION_KEYS } from '../../data/userPermissions';

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtCurrency = (n) =>
  n != null ? `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '₹0';

const fmtDate = (val) => {
  if (!val) return '—';
  try {
    return new Date(val).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata',
    });
  } catch { return val; }
};

const fmtMT = (mt) =>
  mt != null ? `${Number(mt).toFixed(1)} MT` : '—';

const EARN_STATUSES = ['do_generated', 'completed'];

// ── Sub-components ────────────────────────────────────────────────────────────
const StatCard = ({ icon, label, value, sub, accent }) => (
  <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 flex items-start gap-3">
    <div
      className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center text-lg"
      style={{ background: accent + '18', color: accent }}
    >
      <Icon name={icon} size={20} />
    </div>
    <div className="min-w-0">
      <p className="text-xs text-gray-500 mb-0.5">{label}</p>
      <p className="text-lg font-bold text-gray-900 leading-tight truncate">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  </div>
);

const Badge = ({ text, color, bg }) => (
  <span
    className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap"
    style={{ color, background: bg }}
  >
    {text}
  </span>
);

const statusBadge = (status) => {
  const map = {
    approved:     { text: 'Approved',    color: '#166534', bg: '#dcfce7' },
    do_generated: { text: 'DO Issued',   color: '#1e40af', bg: '#dbeafe' },
    completed:    { text: 'Completed',   color: '#374151', bg: '#f3f4f6' },
    pending:      { text: 'Pending',     color: '#92400e', bg: '#fef3c7' },
    rejected:     { text: 'Rejected',    color: '#991b1b', bg: '#fee2e2' },
  };
  const s = map[status] || { text: status || '—', color: '#374151', bg: '#f3f4f6' };
  return <Badge {...s} />;
};

const TabBtn = ({ label, active, onClick, count }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
      active
        ? 'bg-blue-50 text-blue-700 border border-blue-200'
        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
    }`}
  >
    {label}
    {count != null && (
      <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${
        active ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
      }`}>
        {count}
      </span>
    )}
  </button>
);

// ── Register Buyer Form ───────────────────────────────────────────────────────
const RegisterBuyerForm = ({ agentUserId, onSuccess }) => {
  const [form, setForm] = useState({ full_name: '', email: '', mobile_phone: '', gstin: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleChange = (e) => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleSubmit = async () => {
    const { full_name, email, mobile_phone, gstin } = form;
    if (!full_name.trim() || !email.trim() || !mobile_phone.trim() || !gstin.trim()) {
      setError('All fields are required.');
      return;
    }
    const phoneDigits = mobile_phone.replace(/\D/g, '');
    if (phoneDigits.length < 10) { setError('Enter a valid 10-digit mobile number.'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) { setError('Enter a valid email address.'); return; }
    if (gstin.trim().length !== 15) { setError('GSTIN must be 15 characters.'); return; }

    setLoading(true);
    setError('');

    try {
      // Generate a random temporary PIN — buyer will reset via email OTP
      const tempPin = String(Math.floor(1000 + Math.random() * 9000));
      const normalizedPhone = '+91' + phoneDigits.slice(-10);

      const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
      const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

      // Step 1: Register buyer via pwa-user-signup
      const signupRes = await fetch(`${SUPABASE_URL}/functions/v1/pwa-user-signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'apikey': SUPABASE_ANON_KEY },
        body: JSON.stringify({
          role: 'buyer',
          Phone: normalizedPhone,
          full_name: full_name.trim(),
          email: email.trim().toLowerCase(),
          pin: tempPin,
          confirm_pin: tempPin,
          gstin: gstin.trim().toUpperCase(),
          referred_by_agent_id: agentUserId,
        }),
      });

      const signupData = await signupRes.json();
      if (!signupRes.ok || !signupData.success) {
        setError(signupData.error || 'Registration failed. Please try again.');
        setLoading(false);
        return;
      }

      // Step 2: Send forgot-pin OTP so buyer can set their own PIN
      await fetch(`${SUPABASE_URL}/functions/v1/forgot-pin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'apikey': SUPABASE_ANON_KEY },
        body: JSON.stringify({ action: 'send_otp', Phone: normalizedPhone }),
      });

      setSuccess(`Buyer registered! An OTP has been sent to ${email.trim()} for them to set their login PIN.`);
      setForm({ full_name: '', email: '', mobile_phone: '', gstin: '' });
      if (onSuccess) onSuccess();
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-lg">
      <p className="text-sm text-gray-500 mb-5">
        Register a buyer under your account. Every booking they place earns you a commission.
        They'll receive an OTP to set their own login PIN.
      </p>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
          ✓ {success}
        </div>
      )}

      <div className="space-y-4">
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Company / Buyer Name</label>
          <input
            name="full_name"
            value={form.full_name}
            onChange={handleChange}
            placeholder="e.g. Ravi Sugars Pvt Ltd"
            className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">WhatsApp Number of Buyer</label>
          <input
            name="mobile_phone"
            value={form.mobile_phone}
            onChange={handleChange}
            placeholder="Buyer's WhatsApp number"
            maxLength={10}
            className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Email Address</label>
          <input
            name="email"
            value={form.email}
            onChange={handleChange}
            placeholder="buyer@example.com"
            type="email"
            className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">GSTIN</label>
          <input
            name="gstin"
            value={form.gstin}
            onChange={(e) => handleChange({ target: { name: 'gstin', value: e.target.value.toUpperCase() } })}
            placeholder="15-character GSTIN"
            maxLength={15}
            className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
        </div>

        <button
          onClick={handleSubmit}
          disabled={loading}
          className="w-full py-2.5 rounded-lg text-sm font-semibold text-white transition-colors"
          style={{ background: loading ? '#9ca3af' : '#1e4a72' }}
        >
          {loading ? 'Registering...' : 'Register Buyer'}
        </button>
      </div>
    </div>
  );
};

// ── Main Dashboard ────────────────────────────────────────────────────────────
const SalesAgentDashboard = () => {
  const [activeTab, setActiveTab] = useState('buyers');
  const navigate = useNavigate();
  const [showGstinGate, setShowGstinGate] = useState(false);
  const [gstinGateStep, setGstinGateStep] = useState('prompt');
  const [gstinInput, setGstinInput] = useState('');
  const [gstinError, setGstinError] = useState('');
  const [gstinVerified, setGstinVerified] = useState(null);
  const [upgrading, setUpgrading] = useState(false);
  const [upgradeError, setUpgradeError] = useState('');

  const handleDoFinanceClick = () => {
    if (agent?.gstin) {
      navigate('/do-finance/apply');
    } else {
      setGstinGateStep('prompt');
      setGstinInput('');
      setGstinError('');
      setGstinVerified(null);
      setShowGstinGate(true);
    }
  };

  const handleVerifyGstin = async () => {
    const g = gstinInput.trim().toUpperCase();
    if (g.length !== 15) { setGstinError('GSTIN must be 15 characters'); return; }
    setGstinGateStep('verifying'); setGstinError('');
    try {
      const { data, error } = await supabase.functions.invoke('verify-gstin', { body: { gstin: g } });
      if (error || !data?.success) throw new Error(data?.error || 'Verification failed');
      setGstinVerified(data);
      setGstinGateStep('confirmed');
    } catch (err) {
      setGstinError(err.message ?? 'Verification failed. Try again.');
      setGstinGateStep('input');
    }
  };

  const handleConfirmGstin = async () => {
    setGstinGateStep('saving');
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { error } = await supabase.from('user_profiles').update({ gstin: gstinVerified.gstin }).eq('user_id', user.id);
      if (error) throw error;
      setShowGstinGate(false);
      navigate('/do-finance/apply');
    } catch (err) {
      if (err?.code === '23505' || err?.message?.includes('user_profiles_gstin_unique')) {
        setGstinError('This GSTIN is already registered to another SugarHathi account. If this is a mistake, please contact support.');
      } else {
        setGstinError(err.message ?? 'Failed to save. Try again.');
      }
      setGstinGateStep('confirmed');
    }
  };

  const handleUpgradeToBuyer = async () => {
    setUpgrading(true);
    setUpgradeError('');
    try {
      const { data, error } = await supabase.functions.invoke('sales-agent-upgrade-to-buyer');
      if (error || !data?.success) throw new Error(data?.error || error?.message || 'Upgrade failed. Try again.');
      // Role changed server-side — reload so AuthContext picks up the new profile.
      window.location.reload();
    } catch (err) {
      setUpgradeError(err.message ?? 'Upgrade failed. Try again.');
      setUpgrading(false);
    }
  };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [agent, setAgent] = useState(null);
  const [buyers, setBuyers] = useState([]);
  const [bookings, setBookings] = useState([]);
  // Admin-controlled per-user flag (user_permissions.permission_key='do_finance'),
  // set via the DO Finance toggle in Admin Dashboard > Users. Defaults to false
  // (fail-closed) so a not-yet-resolved or errored check never shows the button.
  const [doFinanceEnabled, setDoFinanceEnabled] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // Current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { setError('Not authenticated.'); setLoading(false); return; }

      // Agent profile — accepts role='sales_agent' or an upgraded
      // (was_sales_agent=true) buyer, matching ProtectedRoute's flag check
      const { data: agentProfile, error: agentErr } = await supabase
        .from('user_profiles')
        .select('user_id, full_name, company_name, email, mobile_phone, commission_rate_per_ton, created_at, gstin, was_sales_agent, role')
        .eq('user_id', user.id)
        .single();

      if (agentErr || !agentProfile || (agentProfile.role !== 'sales_agent' && !agentProfile.was_sales_agent)) {
        setError('Agent profile not found.');
        setLoading(false);
        return;
      }
      setAgent(agentProfile);

      // Referred buyers + admin-controlled DO Finance eligibility, fetched
      // together so the button's visibility is resolved before `loading`
      // flips false — no flash of the button before it's hidden.
      const [{ data: buyerRows }, doFinanceOn] = await Promise.all([
        supabase
          .from('user_profiles')
          .select('user_id, full_name, company_name, email, mobile_phone, gstin, created_at, kyc_status, commission_rate_per_ton')
          .eq('referred_by_agent_id', agentProfile.user_id)
          .eq('role', 'buyer')
          .order('created_at', { ascending: false }),
        hasPermission(agentProfile.user_id, PERMISSION_KEYS.DO_FINANCE),
      ]);
      setDoFinanceEnabled(doFinanceOn);

      const buyerList = buyerRows || [];
      setBuyers(buyerList);

      // Bookings from referred buyers
      if (buyerList.length > 0) {
        const buyerIds = buyerList.map(b => b.user_id);
        const { data: bookingRows } = await supabase
          .from('buyer_bookings')
          .select('readable_booking_id, buyer_id, quantity, status, created_at')
          .in('buyer_id', buyerIds)
          .in('status', EARN_STATUSES)
          .order('created_at', { ascending: false });

        setBookings(bookingRows || []);
      } else {
        setBookings([]);
      }
    } catch (err) {
      setError('Failed to load data. Please refresh.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Buyer lookup maps (must be before computed stats) ────────────────────
  const buyerMap = Object.fromEntries(buyers.map(b => [b.user_id, b.company_name || b.full_name || '—']));
  const buyerRateMap = Object.fromEntries(buyers.map(b => [b.user_id, b.commission_rate_per_ton ?? 10]));

  // ── Computed stats ───────────────────────────────────────────────────────
  const totalMT = bookings.reduce((s, b) => s + Number(b.quantity || 0), 0);
  const totalEarnings = bookings.reduce((sum, b) => {
    const mt = Number(b.quantity || 0);
    const rate = buyerRateMap[b.buyer_id] ?? 10;
    return sum + mt * rate;
  }, 0);

  // This month
  const thisMonthStart = new Date();
  thisMonthStart.setDate(1);
  thisMonthStart.setHours(0, 0, 0, 0);
  const thisMonthMT = bookings
    .filter(b => new Date(b.created_at) >= thisMonthStart)
    .reduce((s, b) => s + Number(b.quantity || 0), 0);
  const thisMonthEarnings = bookings
    .filter(b => new Date(b.created_at) >= thisMonthStart)
    .reduce((sum, b) => {
      const mt = Number(b.quantity || 0);
      const rate = buyerRateMap[b.buyer_id] ?? 10;
      return sum + mt * rate;
    }, 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-3" />
          <p className="text-sm text-gray-500">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-red-100 max-w-sm text-center">
          <Icon name="AlertCircle" size={32} className="mx-auto mb-3 text-red-500" />
          <p className="text-sm text-red-700">{error}</p>
          <button onClick={loadData} className="mt-4 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Sales Agent Dashboard – SugarHathi</title>
      </Helmet>

      <RoleBasedHeader />

      <div className="min-h-screen bg-gray-50 pb-10">

        {/* ── Top banner ── */}
        <div style={{ background: 'linear-gradient(135deg,#1e4a72,#15375a)' }} className="px-4 pt-5 pb-16">
          <p className="text-xs text-blue-300 mb-0.5">Sales Agent</p>
          <h1 className="text-xl font-bold text-white">
            {agent?.company_name || agent?.full_name || 'Agent Dashboard'}
          </h1>
          <p className="text-xs text-blue-300 mt-0.5">
            {buyers.length} buyer{buyers.length !== 1 ? 's' : ''} registered · commission varies per buyer
          </p>
        </div>

        <div className="px-4 -mt-10">

          {/* ── Stats strip ── */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <StatCard icon="Users" label="Buyers Registered" value={buyers.length} sub="All time" accent="#1e4a72" />
            <StatCard icon="Package" label="Total MT Booked" value={`${totalMT.toFixed(1)} MT`} sub="Across all buyers" accent="#7c3aed" />
            <StatCard icon="IndianRupee" label="Total Earnings" value={fmtCurrency(totalEarnings)} sub="Across all buyers" accent="#16a34a" />
            <StatCard icon="TrendingUp" label="This Month" value={fmtCurrency(thisMonthEarnings)} sub={`${thisMonthMT.toFixed(1)} MT`} accent="#d97706" />
          </div>

          {/* ── DO Finance Button (admin-gated: user_permissions.do_finance) ── */}
          {doFinanceEnabled && (
            <button
              onClick={handleDoFinanceClick}
              className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm font-bold text-white mb-4 shadow-md transition-all hover:opacity-90 active:scale-95"
              style={{ background: 'linear-gradient(135deg, #f97316, #ea580c)' }}
            >
              <Icon name="Gavel" size={16} />
              Need DO Finance?
            </button>
          )}

          {/* ── Upgrade to Buyer ── */}
          {agent?.was_sales_agent ? (
            <div className="w-full flex items-center gap-2 px-4 py-3 rounded-xl text-sm mb-4 bg-green-50 border border-green-200 text-green-800">
              <Icon name="CheckCircle2" size={16} />
              Buyer trading enabled — your sales agent dashboard and commission history stay accessible.
            </div>
          ) : agent?.gstin ? (
            <div className="mb-4">
              <button
                onClick={handleUpgradeToBuyer}
                disabled={upgrading}
                className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm font-bold text-white shadow-md transition-all hover:opacity-90 active:scale-95 disabled:opacity-60"
                style={{ background: 'linear-gradient(135deg, #1e4a72, #15375a)' }}
              >
                <Icon name="ArrowUpCircle" size={16} />
                {upgrading ? 'Enabling...' : 'Enable Buyer Trading'}
              </button>
              {upgradeError && <p className="text-xs text-red-600 mt-1.5">{upgradeError}</p>}
            </div>
          ) : doFinanceEnabled ? (
            <p className="text-xs text-gray-400 mb-4 px-1">
              Add your GSTIN (via "Need DO Finance?" above) to unlock buyer trading on your account.
            </p>
          ) : null}

          {/* ── Tabs ── */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="flex gap-1 p-2 border-b border-gray-100 overflow-x-auto">
              <TabBtn label="My Buyers" active={activeTab === 'buyers'} onClick={() => setActiveTab('buyers')} count={buyers.length} />
              <TabBtn label="My Earnings" active={activeTab === 'earnings'} onClick={() => setActiveTab('earnings')} count={bookings.length} />
              <TabBtn label="Register Buyer" active={activeTab === 'register'} onClick={() => setActiveTab('register')} />
            </div>

            <div className="p-4">

              {/* ── My Buyers tab ── */}
              {activeTab === 'buyers' && (
                <>
                  {buyers.length === 0 ? (
                    <div className="text-center py-10">
                      <Icon name="UserPlus" size={32} className="mx-auto mb-3 text-gray-300" />
                      <p className="text-sm font-medium text-gray-500">No buyers registered yet</p>
                      <p className="text-xs text-gray-400 mt-1">Use "Register Buyer" to add your first buyer.</p>
                      <button
                        onClick={() => setActiveTab('register')}
                        className="mt-4 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg font-medium"
                      >
                        Register First Buyer
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {buyers.map(b => {
                        const bBookings = bookings.filter(bk => bk.buyer_id === b.user_id);
                        const bMT = bBookings.reduce((s, bk) => s + Number(bk.quantity || 0), 0);
                        const bEarn = bMT * (b.commission_rate_per_ton ?? 10);
                        const isActive = bookings.some(bk =>
                          bk.buyer_id === b.user_id &&
                          ['do_generated', 'completed'].includes(bk.status)
                        );
                        return (
                          <div key={b.user_id} className="border border-gray-100 rounded-lg p-3">
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <div className="min-w-0">
                                <p className="text-sm font-semibold text-gray-900 truncate">
                                  {b.company_name || b.full_name || '—'}
                                </p>
                                <p className="text-xs text-gray-500">{b.mobile_phone} · {b.email}</p>
                              </div>
                              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                <Badge
                                  text={isActive ? '● Active' : '○ Inactive'}
                                  color={isActive ? '#166534' : '#6b7280'}
                                  bg={isActive ? '#dcfce7' : '#f3f4f6'}
                                />
                                {b.kyc_status === 'verified'
                                  ? <Badge text="KYC ✓" color="#166534" bg="#dcfce7" />
                                  : <Badge text="KYC Pending" color="#92400e" bg="#fef3c7" />
                                }
                              </div>
                            </div>
                            <div className="flex items-center gap-4 text-xs text-gray-500">
                              <span>{bBookings.length} booking{bBookings.length !== 1 ? 's' : ''}</span>
                              <span>{bMT.toFixed(1)} MT</span>
                              <span className="font-semibold text-green-700">{fmtCurrency(bEarn)} earned</span>
                              <span className="ml-auto">Registered {fmtDate(b.created_at)}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}

              {/* ── My Earnings tab ── */}
              {activeTab === 'earnings' && (
                <>
                  {/* Summary bar */}
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4 flex items-center justify-between">
                    <div>
                      <p className="text-xs text-green-700 font-medium">Total Accrued Earnings</p>
                      <p className="text-xl font-bold text-green-800">{fmtCurrency(totalEarnings)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-green-600">{totalMT.toFixed(1)} MT · rate varies per buyer</p>
                      <p className="text-xs text-green-600">{bookings.length} qualifying booking{bookings.length !== 1 ? 's' : ''}</p>
                    </div>
                  </div>

                  {bookings.length === 0 ? (
                    <div className="text-center py-8">
                      <Icon name="IndianRupee" size={32} className="mx-auto mb-3 text-gray-300" />
                      <p className="text-sm text-gray-500">No earnings yet</p>
                      <p className="text-xs text-gray-400 mt-1">Earnings appear when your buyers place approved bookings.</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {bookings.map((b, i) => {
                        const mt = Number(b.quantity || 0);
                        const earn = mt * (buyerRateMap[b.buyer_id] ?? 10);
                        return (
                          <div key={b.readable_booking_id || i} className="border border-gray-100 rounded-lg p-3">
                            <div className="flex items-start justify-between gap-2 mb-1">
                              <div className="min-w-0">
                                <p className="text-xs font-mono font-semibold text-gray-700 truncate">
                                  {b.readable_booking_id || '—'}
                                </p>
                                <p className="text-xs text-gray-500">{buyerMap[b.buyer_id] || '—'}</p>
                              </div>
                              {statusBadge(b.status)}
                            </div>
                            <div className="flex items-center gap-4 text-xs text-gray-500 mt-1">
                              <span>{mt.toFixed(1)} MT</span>
                              <span>× ₹{buyerRateMap[b.buyer_id] ?? 10}/tonne</span>
                              <span className="font-semibold text-green-700">{fmtCurrency(earn)}</span>
                              <span className="ml-auto">{fmtDate(b.created_at)}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}

              {/* ── Register Buyer tab ── */}
              {activeTab === 'register' && (
                <RegisterBuyerForm
                  agentUserId={agent?.user_id}
                  onSuccess={() => {
                    loadData();
                    setActiveTab('buyers');
                  }}
                />
              )}

            </div>
          </div>
        </div>
      </div>
      {showGstinGate && (
        <div className="fixed inset-0 z-50 flex items-end justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>
          <div className="bg-white w-full max-w-lg rounded-t-3xl shadow-2xl px-5 pt-5 pb-8">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg,#f97316,#ea580c)' }}>
                  <Icon name="Gavel" size={18} color="#fff" />
                </div>
                <div>
                  <h2 className="font-bold text-base" style={{ color: '#1f2937' }}>DO Finance</h2>
                  <p className="text-xs" style={{ color: '#6b7280' }}>GSTIN required to proceed</p>
                </div>
              </div>
              <button onClick={() => setShowGstinGate(false)} className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-gray-100">
                <Icon name="X" size={18} color="#6b7280" />
              </button>
            </div>

            {gstinGateStep === 'prompt' && (
              <div className="space-y-4">
                <div className="flex flex-col items-center text-center py-4">
                  <div className="w-14 h-14 rounded-full flex items-center justify-center mb-3" style={{ backgroundColor: '#fff7ed' }}>
                    <Icon name="FileText" size={26} color="#ea580c" />
                  </div>
                  <h3 className="font-bold text-base mb-1" style={{ color: '#1f2937' }}>GSTIN is mandatory for DO Finance</h3>
                  <p className="text-sm" style={{ color: '#6b7280' }}>Your GSTIN is needed to verify your business before applying.</p>
                </div>
                <button onClick={() => setGstinGateStep('input')}
                  className="w-full py-3.5 rounded-xl text-sm font-bold text-white flex items-center justify-center gap-2"
                  style={{ background: 'linear-gradient(135deg,#f97316,#ea580c)' }}>
                  <Icon name="Plus" size={16} />Yes, Update GSTIN
                </button>
                <button onClick={() => setShowGstinGate(false)}
                  className="w-full py-3 rounded-xl text-sm font-semibold border-2 border-gray-200 text-gray-500">
                  Not Interested
                </button>
              </div>
            )}

            {gstinGateStep === 'input' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold mb-1.5" style={{ color: '#374151' }}>YOUR GSTIN</label>
                  <input type="text" value={gstinInput}
                    onChange={e => { setGstinInput(e.target.value.toUpperCase()); setGstinError(''); }}
                    placeholder="e.g. 29ABCDE1234F1Z5" maxLength={15}
                    className="w-full px-4 py-3 rounded-xl border-2 text-sm font-mono focus:outline-none"
                    style={{ borderColor: gstinError ? '#dc2626' : '#e5e7eb', color: '#1f2937' }}
                  />
                  {gstinError && <p className="text-xs mt-1" style={{ color: '#dc2626' }}>{gstinError}</p>}
                  <p className="text-xs mt-1.5" style={{ color: '#9ca3af' }}>15-character GST Identification Number</p>
                </div>
                <button onClick={handleVerifyGstin} disabled={gstinInput.trim().length !== 15}
                  className="w-full py-3.5 rounded-xl text-sm font-bold text-white flex items-center justify-center gap-2 disabled:opacity-50"
                  style={{ background: 'linear-gradient(135deg,#f97316,#ea580c)' }}>
                  <Icon name="Search" size={16} />Verify GSTIN
                </button>
              </div>
            )}

            {(gstinGateStep === 'verifying' || gstinGateStep === 'saving') && (
              <div className="flex flex-col items-center py-10 gap-3">
                <div className="w-8 h-8 border-2 border-orange-400 border-t-transparent rounded-full animate-spin" />
                <p className="text-sm" style={{ color: '#6b7280' }}>
                  {gstinGateStep === 'verifying' ? 'Verifying GSTIN...' : 'Saving your GSTIN...'}
                </p>
              </div>
            )}

            {gstinGateStep === 'confirmed' && gstinVerified && (
              <div className="space-y-4">
                <div className="p-4 rounded-xl border-2" style={{ borderColor: '#bbf7d0', backgroundColor: '#f0fdf4' }}>
                  <div className="flex items-center gap-2 mb-3">
                    <Icon name="CheckCircle" size={18} color="#16a34a" />
                    <p className="text-sm font-bold" style={{ color: '#16a34a' }}>GSTIN Verified</p>
                  </div>
                  <p className="text-base font-bold mb-0.5" style={{ color: '#1f2937' }}>{gstinVerified.trade_name || gstinVerified.legal_name}</p>
                  {gstinVerified.trade_name && gstinVerified.legal_name !== gstinVerified.trade_name && (
                    <p className="text-xs mb-2" style={{ color: '#6b7280' }}>{gstinVerified.legal_name}</p>
                  )}
                  <div className="flex gap-2 mt-2">
                    <span className="px-2 py-1 rounded-md text-xs font-semibold" style={{ backgroundColor: '#dcfce7', color: '#15803d' }}>{gstinVerified.gstin_status}</span>
                    <span className="px-2 py-1 rounded-md text-xs font-semibold" style={{ backgroundColor: '#fff7ed', color: '#ea580c' }}>{gstinVerified.taxpayer_type}</span>
                  </div>
                </div>
                {gstinError && <p className="text-xs" style={{ color: '#dc2626' }}>{gstinError}</p>}
                <button onClick={handleConfirmGstin}
                  className="w-full py-3.5 rounded-xl text-sm font-bold text-white flex items-center justify-center gap-2"
                  style={{ background: 'linear-gradient(135deg,#f97316,#ea580c)' }}>
                  <Icon name="ArrowRight" size={16} />Confirm & Apply for DO Finance
                </button>
                <button onClick={() => { setGstinGateStep('input'); setGstinError(''); }}
                  className="w-full py-2.5 text-sm font-semibold text-gray-500">
                  Re-enter GSTIN
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default SalesAgentDashboard;
